import { Segmented as AntDesignSegmented, Tooltip } from 'antd';
import cx from 'classnames';
import { isEmpty, uniq, values } from 'ramda';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdManageSearch,
  MdOutlineAutoAwesome,
  MdOutlineInsights,
  MdOutlineLocationOn,
  MdOutlineSettingsSuggest,
  MdTrendingUp,
} from 'react-icons/md';
import { connect } from 'react-redux';

import {
  ROUTES_WITH_INSIGHTS,
  ROUTES_WITH_INSIGHTS_FOR_TRIAL_ORGANIZATION,
} from '../../AppRoutes';
import { features } from '../../pages/dashboards/dashboardFramework/helpers/features';
import styles from './_MenuBar.module.scss';

export const Modules = {
  myPlaces: 'my-places',
  chaosAi: 'chaos-ai',
  aiReports: 'ai-reports',
  discover: 'discover',
  assess: 'assess',
  optimize: 'optimize',
};

export type MenuOption = (typeof Modules)[keyof typeof Modules];

const menuBarSortOrder = values(Modules);

type MenuBarProps = {
  selected?: MenuOption;
  onChange: (value: MenuOption) => void;
  globalFeatures: string[];
  isSelfSignUp?: boolean;
};

const iconMap: Record<MenuOption, React.ElementType> = {
  'my-places': MdOutlineLocationOn,
  discover: MdManageSearch,
  assess: MdTrendingUp,
  optimize: MdOutlineSettingsSuggest,
  'chaos-ai': MdOutlineAutoAwesome,
  'ai-reports': MdOutlineInsights,
};

const MenuBar: React.FC<MenuBarProps> = ({
  selected,
  onChange,
  globalFeatures,
  isSelfSignUp,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'dashboards' });

  const renderMenuOption = useCallback(
    (key: string) => {
      const MenuIcon = iconMap[key as MenuOption];

      return {
        label: (
          <Tooltip placement="right" title={t(`tooltips.${key}`)}>
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
              <MenuIcon size={24} />
              <div className="tw-text-xs">{t(key)}</div>
            </div>
          </Tooltip>
        ),
        value: key,
      };
    },
    [t],
  );

  const menuOptions = useMemo(() => {
    const menuBarOptions = [];

    const hasChaosAi =
      !isSelfSignUp || globalFeatures?.includes(features.CHAOS_AI);

    const hasAiReports = globalFeatures?.includes(features.AI_REPORTS);

    const hasPoints = globalFeatures?.includes(features.POINTS);

    if (hasPoints) {
      menuBarOptions.push(Modules.myPlaces);
    }

    if (hasAiReports) {
      menuBarOptions.push(features.AI_REPORTS);
    }

    if (hasChaosAi) {
      menuBarOptions.push(features.CHAOS_AI);
    }

    const assignedBundles = isSelfSignUp
      ? ROUTES_WITH_INSIGHTS_FOR_TRIAL_ORGANIZATION
      : ROUTES_WITH_INSIGHTS;

    const options = uniq([...menuBarOptions, ...assignedBundles]);

    // Show Optimize as only module if user's license have expired.
    const assignedModuleNames = !isEmpty(options)
      ? options
      : [Modules.optimize];

    return assignedModuleNames
      .map(renderMenuOption)
      .sort(
        (a: any, b: any) =>
          menuBarSortOrder.indexOf(a.value) - menuBarSortOrder.indexOf(b.value),
      );
  }, [globalFeatures, isSelfSignUp, renderMenuOption]);

  return (
    <AntDesignSegmented
      vertical
      size="middle"
      className={cx(
        'tw-select-none',
        styles.menuBarItem,
        styles.menuBarContainer,
      )}
      options={menuOptions}
      value={selected}
      defaultValue={undefined}
      onChange={onChange}
    />
  );
};

const mapStateToProps = (state: any) => ({
  globalFeatures: state?.user?.globalFeatures || [],
  isSelfSignUp: state?.user?.info?.isSelfSignUp,
});

export default connect(mapStateToProps)(MenuBar);
